<template>
  <detail-card :section-key="sectionKey">
    <b-row class="detail-field-row" v-for="field in displayFields" :key="field.key">
      <b-col :cols="indicatorLayout ? 8 : 7" :class="{ 'font-weight-bold': !indicatorLayout }">{{ label(field) }}</b-col>
      <b-col :cols="indicatorLayout ? 4 : 5" :class="valueClass(field)">
        <!-- :checked (not a hardcoded true) follows the other boolean radios
        (AdditionalFeatures.vue's v-model="feature.value", DataRow.vue's v-model="opt.value" — both
        only check the dot when the value is exactly `true`): a hardcoded `:checked="true"` would
        render the dot filled/colored for every value, false and null included, instead of only for
        a genuinely `true` field (a null field, e.g. an absent ComplexityAlert, must show the same
        grey/unchecked look `false` gets). -->
        <b-form-radio
          v-if="displayValue(field).isBoolean"
          :checked="displayValue(field).boolValue === true"
          :value="true"
          disabled
        >
          <span>{{ displayValue(field).boolValue ? $t('common.present') : $t('common.absent') }}</span>
        </b-form-radio>
        <span v-else>{{ displayValue(field).text }}</span>
      </b-col>
    </b-row>
  </detail-card>
</template>

<script>
// Generic renderer for a field-catalog-driven DetailPageSection (`{ sectionKey, fields }`),
// the section-level sibling of ../../utils/detailFieldFormat.js. Every field is rendered the same
// way regardless of asset class — see DetailSection.vue for the dispatch point that routes here vs.
// a typed component (DETAIL_PAGE_DESIGN_FE.md). The card shell (border/background/header) lives
// in DetailCard.vue, shared with typed sections that need the same box treatment (e.g.
// typedSections/LiquiditySection.vue).
import DetailCard from './DetailCard'
import { detailFieldLabel, detailFieldDisplayValue, mergeRhpFields } from '../../utils/detailFieldFormat'

// Old-page sections sharing this same "flat field bag" shape don't all share the same layout:
// ProductCharacteristics.vue (Characteristics/CapitalProtection/Coupons/EarlyRedemption's closest
// precedent) uses a 7/5 split, bold label, left-aligned value; MarketIndices.vue uses 8/4, no bold,
// right-aligned value instead — confirmed by reading both components directly, not guessed. No
// backend signal (field_type) distinguishes these, so the section_key list here is this component's
// own presentation choice, same precedent as FilterField.vue's SriRange special-case.
//
// CostsAtExpiration shares MarketIndicators' catalog shape (variant_group/variant_role pairs, see
// configs_detail_fields) but NOT its layout — it needs a real 2-column
// Min/Max table (1 column when Min==Max, i.e. SOP/SID/IC), not stacked label/value rows — so it gets
// its own typedSections/CostsAtExpirationSection.vue instead of being bucketed in here.
const INDICATOR_LAYOUT_SECTIONS = new Set(['MarketIndicators'])

// Right-aligned value column — a separate axis from INDICATOR_LAYOUT_SECTIONS above (which also drops
// the bold label and widens the label column to 8/4): the three Payout sections keep the 7/5 split and
// the bold label, and only move their values to the right edge, so that tab reads like the KPI KID
// tab's tables (whose values are right/center-aligned) instead of leaving a ragged column mid-row —
// user-requested. MarketIndicators is in both sets: it was already right-aligned before this axis was
// split out, and stays so.
const RIGHT_ALIGNED_VALUE_SECTIONS = new Set(['MarketIndicators', 'CapitalProtection', 'Coupons', 'EarlyRedemption'])

export default {
  name: 'field-catalog-section',
  components: {
    DetailCard,
  },
  props: {
    sectionKey: { type: String, required: true },
    fields: { type: Array, required: true },
  },
  computed: {
    displayFields() {
      return mergeRhpFields(this.fields)
    },
    indicatorLayout() {
      return INDICATOR_LAYOUT_SECTIONS.has(this.sectionKey)
    },
    rightAlignedValue() {
      return RIGHT_ALIGNED_VALUE_SECTIONS.has(this.sectionKey)
    },
  },
  methods: {
    label(field) {
      return detailFieldLabel(this, this.sectionKey, field.key)
    },
    displayValue(field) {
      return detailFieldDisplayValue(this, field)
    },
    // Booleans need the whole `.custom-control` block pushed right, not `text-align` — the segmented
    // control's dot is drawn by the label's ::before at a fixed negative offset inside a
    // padding-left'ed block, so it does not follow a text-align the way a plain value does. This is
    // AdditionalFeatures.vue's own precedent for the same control (`d-flex` + `ml-auto`), expressed
    // here as justify-content-end since the column itself is the flex container.
    valueClass(field) {
      if (!this.rightAlignedValue) {
        return []
      }
      return this.displayValue(field).isBoolean ? ['d-flex', 'justify-content-end'] : ['text-right']
    },
  },
}
</script>

<!-- No <style> here — the generic `.row` vertical spacing (margin-bottom, plus the first/last-row
2rem overrides) lives in DetailCard.vue's `/deep/ .row` rules, shared with every consumer instead of
re-declared per section. See that file for why. -->
